import {
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
} from 'react-admin'
import {
  BARRIERS_URL,
  CAMERA_SERVERS_URL,
  CAMERAS_URL,
  DEPOSITORS_URL,
  DETECTORS_URL,
  DOORS_URL,
  ENGINES_URL,
  IKSORIS_PARKING_TICKET_CASH_MACHINES_URL,
  IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL,
  OFFLINE_SERVERS_URL,
  PALMS_URL,
  PASSAGES_URL,
  SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL,
  TERMINALS_URL,
} from '../../../api-urls'
import { get, getWithPagination } from '../../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../../common/ticket-api-get-list.params'
import { DeviceBaseWithTypeInfoDto } from '../../../dto/device/common/device-base/device-base-with-type-info.dto'
import { DeviceBaseDto } from '../../../dto/device/common/device-base/device-base.dto'
import { DetectorDto } from '../../../dto/device/detector/detector.dto'
import { EngineDto } from '../../../dto/device/engine/engine.dto'
import { OfflineServerDto } from '../../../dto/device/offline-servers/offline-server.dto'
import { PalmDto } from '../../../dto/device/palm/palm.dto'
import { PassageDto } from '../../../dto/device/passages/passage.dto'
import { DeviceTypes } from '../../../enum/DeviceTypes'
import { ResourceName } from '../../../ResourceName'
import { mapping as deviceBaseFilterMapping } from './device-base-filter.mapper'
import mapSortDeviceParam from './device-base-sort.mapper'
import { getDeviceCategoryChildren } from '../../devices.utils'
import { BarrierDto } from '../../../dto/device/barrier/barrier.dto'
import { DepositorDto } from '../../../dto/device/depositor/depositor.dto'
import { DoorDto } from '../../../dto/device/doors/door.dto'
import { TerminalDto } from '../../../dto/device/terminals/terminal.dto'
import { Authority } from '../../../auth/Authority'
import authProvider from '../../../auth/auth.provider'
import { hasAuthorityWithGivenPermissions } from '../../../../main/hooks/useHasAuthority'
import dataProvider from '../../../data.provider'

const deviceBaseFilterMapper = (key: string) =>
  (deviceBaseFilterMapping as Record<string, any>)[key] ?? (() => null)

const mapDeviceType = (
  devices: DeviceBaseDto[],
  deviceType: DeviceTypes,
): DeviceBaseWithTypeInfoDto[] =>
  devices.map((device) => ({
    ...device,
    deviceType,
  })) as DeviceBaseWithTypeInfoDto[]

const deviceConfig = {
  [DeviceTypes.BARRIERS]: BARRIERS_URL,
  [DeviceTypes.CAMERA]: CAMERAS_URL,
  [DeviceTypes.CAMERA_SERVER]: CAMERA_SERVERS_URL,
  [DeviceTypes.DEPOSITOR]: DEPOSITORS_URL,
  [DeviceTypes.DETECTOR]: DETECTORS_URL,
  [DeviceTypes.DOORS]: DOORS_URL,
  [DeviceTypes.ENGINE]: ENGINES_URL,
  [DeviceTypes.IKSORIS_PARKING_TICKET_CASH_MACHINE]:
    IKSORIS_PARKING_TICKET_CASH_MACHINES_URL,
  [DeviceTypes.IKSORIS_PARKING_TICKET_CASHLESS_MACHINE]:
    IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL,
  [DeviceTypes.PALM]: PALMS_URL,
  [DeviceTypes.PASSAGE]: PASSAGES_URL,
  [DeviceTypes.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINE]:
    SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL,
  [DeviceTypes.OFFLINE_SERVER]: OFFLINE_SERVERS_URL,
  [DeviceTypes.TERMINALS]: TERMINALS_URL,
} as const

const provider = {
  getList: async (
    resource: string,
    {
      filter: { deviceTypes, limitDeviceType, ...filter },
      sort,
      pagination,
    }: TicketAPIGetListParams,
  ): Promise<GetListResult<DeviceBaseWithTypeInfoDto>> => {
    const userPermissions = await authProvider.getPermissions({})

    let extendedFilter = filter
    // Tree type filter for device categories
    if (extendedFilter?.categoryIdWithDescendants) {
      const categoriesIds = await getDeviceCategoryChildren(
        extendedFilter.categoryIdWithDescendants,
      )
      extendedFilter = { ...extendedFilter, categoriesIds }
    }
    const deviceTypeFilter = deviceTypes as DeviceTypes[]
    const data: DeviceBaseWithTypeInfoDto[] = []
    // Call all types to get total devices count
    const minimalListParams = {
      filter: extendedFilter,
      pagination: { page: 1, perPage: 1 },
    } as TicketAPIGetListParams
    const countTotal = async (
      deviceType: DeviceTypes,
      resourceName: ResourceName,
      permToCheck,
    ) => {
      const totalCount =
        (!limitDeviceType || limitDeviceType === deviceType) &&
        hasAuthorityWithGivenPermissions(permToCheck, userPermissions) &&
        (deviceTypeFilter === undefined ||
          deviceTypeFilter.some((f) => f === deviceType))
          ? (
              (await dataProvider(
                'getList',
                resourceName,
                minimalListParams,
              )) as { data: unknown[]; total: number }
            )?.total
          : 0
      return totalCount
    }
    const totalBarriers = await countTotal(
      DeviceTypes.BARRIERS,
      ResourceName.BARRIERS,
      Authority.VIEW_BARRIERS,
    )
    const totalCameras = await countTotal(
      DeviceTypes.CAMERA,
      ResourceName.CAMERAS,
      Authority.VIEW_CAMERAS,
    )
    const totalCameraServers = await countTotal(
      DeviceTypes.CAMERA_SERVER,
      ResourceName.CAMERA_SERVERS,
      Authority.VIEW_CAMERA_SERVERS,
    )
    const totalDepositors = await countTotal(
      DeviceTypes.DEPOSITOR,
      ResourceName.DEPOSITORS,
      Authority.VIEW_DEPOSITORS,
    )
    const totalDetectors = await countTotal(
      DeviceTypes.DETECTOR,
      ResourceName.DETECTORS,
      Authority.VIEW_DETECTORS,
    )
    const totalDoors = await countTotal(
      DeviceTypes.DOORS,
      ResourceName.DOORS,
      Authority.VIEW_DOORS,
    )
    const totalEngines = await countTotal(
      DeviceTypes.ENGINE,
      ResourceName.ENGINES,
      Authority.VIEW_ENGINES,
    )
    const totalIksorisTicketCashMachine = await countTotal(
      DeviceTypes.IKSORIS_PARKING_TICKET_CASH_MACHINE,
      ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES,
      Authority.VIEW_IKSORIS_PARKING_TICKET_CASH_MACHINES,
    )
    const totalIksorisTicketCashlessMachine = await countTotal(
      DeviceTypes.IKSORIS_PARKING_TICKET_CASHLESS_MACHINE,
      ResourceName.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES,
      Authority.VIEW_IKSORIS_PARKING_TICKET_CASHLESS_MACHINES,
    )
    const totalOfflineServers = await countTotal(
      DeviceTypes.OFFLINE_SERVER,
      ResourceName.OFFLINE_SERVERS,
      Authority.VIEW_OFFLINE_SERVERS,
    )
    const totalSelfposTicketCashlessMachine = await countTotal(
      DeviceTypes.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINE,
      ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES,
      Authority.VIEW_SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES,
    )
    const totalPalms = await countTotal(
      DeviceTypes.PALM,
      ResourceName.PALMS,
      Authority.VIEW_PALMS,
    )
    const totalPassages = await countTotal(
      DeviceTypes.PASSAGE,
      ResourceName.PASSAGES,
      Authority.VIEW_PASSAGES,
    )
    const totalTerminals = await countTotal(
      DeviceTypes.TERMINALS,
      ResourceName.TERMINALS,
      Authority.VIEW_TERMINALS,
    )

    const totalDevices = [
      { total: totalBarriers, type: DeviceTypes.BARRIERS },
      { total: totalCameras, type: DeviceTypes.CAMERA },
      { total: totalCameraServers, type: DeviceTypes.CAMERA_SERVER },
      { total: totalDepositors, type: DeviceTypes.DEPOSITOR },
      { total: totalDetectors, type: DeviceTypes.DETECTOR },
      { total: totalDoors, type: DeviceTypes.DOORS },
      { total: totalEngines, type: DeviceTypes.ENGINE },
      {
        total: totalIksorisTicketCashMachine,
        type: DeviceTypes.IKSORIS_PARKING_TICKET_CASH_MACHINE,
      },
      {
        total: totalIksorisTicketCashlessMachine,
        type: DeviceTypes.IKSORIS_PARKING_TICKET_CASHLESS_MACHINE,
      },
      { total: totalOfflineServers, type: DeviceTypes.OFFLINE_SERVER },
      { total: totalPalms, type: DeviceTypes.PALM },
      { total: totalPassages, type: DeviceTypes.PASSAGE },
      {
        total: totalSelfposTicketCashlessMachine,
        type: DeviceTypes.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINE,
      },
      { total: totalTerminals, type: DeviceTypes.TERMINALS },
    ]

    const availableEndpointsElementsMap: EndpointCallMapElement[] =
      totalDevices.flatMap(({ total, type }) =>
        Array.from({ length: total }, (_, index) => ({
          index,
          deviceType: type,
        })),
      )

    /* eslint-disable */
    const fromIndex = (pagination.page - 1) * pagination.perPage
    const toIndex = fromIndex + pagination.perPage
    const endpointsCallMap = availableEndpointsElementsMap.slice(
      fromIndex,
      toIndex,
    )

    for (const [devType, url] of Object.entries(deviceConfig)) {
      const dType = devType as DeviceTypes

      // Skip if no endpoints of this type
      if (!endpointsCallMap.some((v) => v.deviceType === dType)) continue

      const filterParams = `o=>${filterParamsComposer(
        'o',
        extendedFilter,
        deviceBaseFilterMapper,
      )}`

      let pathParams = queryParamsComposer(sort, undefined, mapSortDeviceParam)

      // Build range params
      const first = endpointsCallMap.find((v) => v.deviceType === dType)?.index
      const last = endpointsCallMap
        .slice()
        .reverse()
        .find((v) => v.deviceType === dType)?.index

      const rangeParams = [`rangeFrom=${first}`, `rangeTo=${last}`]
      pathParams += `&${rangeParams.join('&')}`

      const path = `/${filterParams}${pathParams ? `?${pathParams}` : ''}`

      // Fetch using pagination
      const { data: items } = await getWithPagination<any[]>(
        `${url}/GetByConditions`,
        path,
      )

      data.push(...mapDeviceType(items, dType))
    }

    return Promise.resolve({
      data,
      total: availableEndpointsElementsMap.length,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<DeviceBaseWithTypeInfoDto>> => {
    try {
      const data = await get<BarrierDto>(BARRIERS_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.BARRIERS },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<DepositorDto>(DEPOSITORS_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.DEPOSITOR },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<DetectorDto>(DETECTORS_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.DETECTOR },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<DoorDto>(DOORS_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.DOORS },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<EngineDto>(ENGINES_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.ENGINE },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<OfflineServerDto>(OFFLINE_SERVERS_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.OFFLINE_SERVER },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<PalmDto>(PALMS_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.PALM },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<PassageDto>(PASSAGES_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.PASSAGE },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
    }

    try {
      const data = await get<TerminalDto>(TERMINALS_URL, `/${id}`)
      return Promise.resolve({
        data: { ...data, deviceType: DeviceTypes.TERMINALS },
      })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error)
      return Promise.reject(error)
    }
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<DeviceBaseWithTypeInfoDto>> => {
    const userPermissions = await authProvider.getPermissions({})
    const hasPermission = (permissionToCheck) =>
      hasAuthorityWithGivenPermissions(permissionToCheck, userPermissions)

    const deviceEndpoints = [
      {
        perm: Authority.VIEW_BARRIERS,
        type: DeviceTypes.BARRIERS,
        url: BARRIERS_URL,
      },
      {
        perm: Authority.VIEW_CAMERAS,
        type: DeviceTypes.CAMERA,
        url: CAMERAS_URL,
      },
      {
        perm: Authority.VIEW_CAMERA_SERVERS,
        type: DeviceTypes.CAMERA_SERVER,
        url: CAMERA_SERVERS_URL,
      },
      {
        perm: Authority.VIEW_DEPOSITORS,
        type: DeviceTypes.DEPOSITOR,
        url: DEPOSITORS_URL,
      },
      {
        perm: Authority.VIEW_DETECTORS,
        type: DeviceTypes.DETECTOR,
        url: DETECTORS_URL,
      },
      { perm: Authority.VIEW_DOORS, type: DeviceTypes.DOORS, url: DOORS_URL },
      {
        perm: Authority.VIEW_ENGINES,
        type: DeviceTypes.ENGINE,
        url: ENGINES_URL,
      },
      {
        perm: Authority.VIEW_OFFLINE_SERVERS,
        type: DeviceTypes.OFFLINE_SERVER,
        url: OFFLINE_SERVERS_URL,
      },
      {
        perm: Authority.VIEW_IKSORIS_PARKING_TICKET_CASH_MACHINES,
        type: DeviceTypes.IKSORIS_PARKING_TICKET_CASH_MACHINE,
        url: IKSORIS_PARKING_TICKET_CASH_MACHINES_URL,
      },
      {
        perm: Authority.VIEW_IKSORIS_PARKING_TICKET_CASHLESS_MACHINES,
        type: DeviceTypes.IKSORIS_PARKING_TICKET_CASHLESS_MACHINE,
        url: IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL,
      },
      { perm: Authority.VIEW_PALMS, type: DeviceTypes.PALM, url: PALMS_URL },
      {
        perm: Authority.VIEW_PASSAGES,
        type: DeviceTypes.PASSAGE,
        url: PASSAGES_URL,
      },
      {
        perm: Authority.VIEW_SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES,
        type: DeviceTypes.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINE,
        url: SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL,
      },
      {
        perm: Authority.VIEW_TERMINALS,
        type: DeviceTypes.TERMINALS,
        url: TERMINALS_URL,
      },
    ]

    const filteredEndpoints = deviceEndpoints.filter(({ perm }) =>
      hasPermission(perm),
    )

    const fetchedData = await Promise.all(
      filteredEndpoints.map(({ type, url }) =>
        get<DeviceBaseDto[]>(
          url,
          `/GetByConditions/e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
        ).then((data) => ({ data, type })),
      ),
    )

    return Promise.resolve({
      data: fetchedData.flatMap(({ data, type }) => mapDeviceType(data, type)),
    })
  },
} as DataProvider

interface EndpointCallMapElement {
  readonly index: number
  readonly deviceType: DeviceTypes
}

export default provider
